import { Injectable } from '@angular/core';
import { AnalysisDisplay } from '../models/analysis-display.model';
import { SpecificationViewModel } from '../models/specification/specification-view-model.model';
import {
  OrderSample,
  PackageQuotationLine,
  TestQuotationLine,
  Test,
  FullOrderDetails,
  CustomerPackage,
  Package
} from '../models/full-order-details.model';
import { Sample } from '../models/sample.model';
import { ParameterSpecification } from '../models/specification/prarameter-specification.model';
import _ from 'lodash';
import { CurrentOrder } from '../models/current-order.model';
import { DisplayPriceService } from 'src/app/features/guided-order/samples/services/display-price.service';
import { File } from '../models/attach-document/file.model';
import { AttachDocument } from '../models/attach-document';
import { AttachDocumentHelperService } from './attach-document-helper.service';
import { StandardComment } from '../models/standard-comment.model';
import { ProductMatrix } from 'projects/dop-ordering/src/lib/models/product-matrix.model';
import { AnalysisType } from 'projects/dop-ordering/src/public-api';
import { FormatterHelperService } from './formatter-helper.service';
import { TranslationService } from 'projects/dop-core/src/public-api';
import { OrderStatus } from '../models/my-orders/order-status.model';

@Injectable({
  providedIn: 'root'
})
export class OrderDetailsMapperService {

  constructor(
    private displayPriceService: DisplayPriceService,
    private attachDocumentHelperService: AttachDocumentHelperService,
    private formatterHelperService: FormatterHelperService) { }

  async mapOrderDetails(data: CurrentOrder, order: FullOrderDetails, attachments: File[]) {
    data.OrderCode = order.orderDetails?.orderCode;
    data.OrderName = order.orderDetails?.name;
    data.OrderReference = order.orderDetails?.orderReference;
    data.CreatedOn = order.orderDetails?.createdOn;
    data.CreatedByName = order.orderDetails?.createdByName;
    data.Email = order.orderDetails?.email;
    data.Status = this.setOrderStatus(order.orderDetails.status);
    data.CustomerReference = order.orderDetails?.customerReference;
    data.CustomerComment = order.orderDetails?.customerComment;
    data.orderTemplate.code = order.account?.orderingServiceCode;
    data.orderTemplate.name = order.account?.orderingServiceName;
    data.quotation = {};
    data.quotation.code = order.quotationContract?.quotationCode;
    data.quotation.name = order.quotationContract?.quotationName;
    data.contract = {};
    data.contract.code = order.quotationContract.contractCode;
    data.contract.name = order.quotationContract.contractName;
    this.mapAccountDetails(data, order);
    this.mapLabDetails(data, order);
    data.additionalFields = order.additionalFields;
    data.displayPrice = await this.checkDisplayPrice(data);
    data.displayShippingPrice = data.displayPrice || order.orderDetails?.shippingEstimatedPrice > 0;
    data.selfShipping = order.orderDetails?.shippingEstimatedPrice === 0 ? true : false;
    data.ShippingCost = this.mapShippingCost(order);
    data.AnalysisCost = this.mapAnalysisCost(order)
    data.OrderTotal = this.mapOrderGrandTotal(order, data.displayPrice);
    data.attachDocuments = this.mapAttachments(attachments);
    data.commentTemplate = order.orderDetails?.commentTemplate;
  }

  private mapAnalysisCost(order: FullOrderDetails): string {
    const analysesCost = order.orderDetails?.estimatedPrice;
    return this.formatterHelperService.formatCurrency(analysesCost, order.orderDetails?.currencyCode + ' ');
  }

  private mapShippingCost(order: FullOrderDetails): string {
    const shipCost = order.orderDetails?.shippingEstimatedPrice;
    return this.formatterHelperService.formatCurrency(shipCost, order.orderDetails?.currencyCode + ' ');
  }

  private mapOrderGrandTotal(order: FullOrderDetails, displayPrice: boolean): string {
    const shippingPrice = order.orderDetails?.shippingEstimatedPrice;
    const orderPrice = order.orderDetails?.estimatedPrice;

    const grandOrderPrice = shippingPrice
      ? displayPrice ? orderPrice + shippingPrice : shippingPrice
      : orderPrice;

    return this.formatterHelperService.formatCurrency(grandOrderPrice, order.orderDetails?.currencyCode + ' ');
  }

  private async checkDisplayPrice(data: CurrentOrder): Promise<boolean> {
    return await this.displayPriceService.checkShowPrice(data.orderTemplate.displayPricing, data.account.code);
  }

  private mapAccountDetails(data: CurrentOrder, order: FullOrderDetails) {
    data.account = {};
    data.account.code = order.account?.accountCode;
    data.account.name = order.account?.accountName;
    data.account.address = {};
    data.account.address.line1 = order.account?.address?.line1;
    data.account.address.line2 = order.account?.address?.line2;
    data.account.address.line3 = order.account?.address?.line3;
    data.account.address.line4 = order.account?.address?.line4;
    data.account.address.line5 = order.account?.address?.line5;
    data.account.address.line6 = order.account?.address?.line6;
    data.account.address.city = order.account?.address?.city;
    data.account.address.zipCode = order.account?.address?.zipCode;
    data.account.address.state = order.account?.address?.state;
    data.account.address.country = order.account?.address?.country;
    data.account.phoneNumber = order.account?.address?.phone;
  }

  private mapLabDetails(data: CurrentOrder, order: FullOrderDetails) {
    data.labDetails = {};
    data.labDetails.code = order.quotationContract?.receivingLabAddress?.code;
    data.labDetails.name = order.quotationContract?.receivingLabAddress?.name;
    data.labDetails.streetNumberAndStreet = order.quotationContract?.receivingLabAddress?.streetNumberAndStreet;
    data.labDetails.city = order.quotationContract?.receivingLabAddress?.city;
    data.labDetails.state = order.quotationContract?.receivingLabAddress?.state;
    data.labDetails.zipCode = order.quotationContract?.receivingLabAddress?.zipCode;
    data.labDetails.country = order.quotationContract?.receivingLabAddress?.country;
    data.labDetails.type = order.quotationContract?.receivingLabAddress?.type;
  }

  mapSampleDetails(samples: OrderSample[], currencyCode: string, location: string, attachments?: File[]) {
    const sampleList: Sample[] = [];
    samples.forEach(x => {
      const sample: Sample = {};
      sample.sampleCode = x.sampleDetails.id;
      sample.customerReference = x.sampleDetails.code;
      sample.comment = x.sampleDetails.comment;
      sample.description = x.sampleDetails.description;
      sample.lotNumber = x.sampleDetails.lotNumber;
      sample.eurofinsSampleCode = x.sampleDetails.sampleReference;
      sample.barcode = x.sampleDetails.containerBarcode;
      sample.priorityLevel = x.sampleDetails.priorityLevel;
      sample.price = this.formatterHelperService.formatCurrency(x.sampleDetails.estimatedPrice, currencyCode + ' ');
      sample.additionalFields = x.additionalFields;
      sample.location = location;
      sample.servingSize = String(x.sampleDetails.servingSize);
      sample.servingUnitNumeratorCode = x.sampleDetails.servingUnitCode;
      sample.servingUnitNumeratorName = x.sampleDetails.servingUnitName;
      sample.product = new ProductMatrix();
      sample.product.productCode = x.sampleDetails.productCode;
      sample.product.productName = x.sampleDetails.productName;
      sample.analysisDisplay =
        this.getTestQuotationLines(x.invoiceableItem.testQuotationLines.filter(test => test.groupCode === undefined));
      this.getSubGroups(sample.analysisDisplay, x.invoiceableItem.testQuotationLines,
        x.invoiceableItem.packageQuotationLines);

      sample.analysisDisplay = this.assignAnalysis(sample.analysisDisplay);
      for (const n in x.invoiceableItem.testQuotationLines) {
        if (x.invoiceableItem.testQuotationLines[n].comment != null) {
          if (x.invoiceableItem.testQuotationLines[n].groupName === undefined) {  // free test quotation line
            const analyses = sample.analysisDisplay.filter(a => a.code === x.invoiceableItem.testQuotationLines[n].testCode)[0];
            if (analyses) {
              analyses.structuredComment = new StandardComment();
              analyses.structuredComment = { code: null, value: x.invoiceableItem.testQuotationLines[n]?.comment };
            }
          } else { // test quotation line under a sub-group
            const analyses = sample.analysisDisplay.filter(a => a.displayName === x.invoiceableItem.testQuotationLines[n].groupName)[0];
            if (analyses) {
              analyses.structuredComment = new StandardComment();
              analyses.structuredComment = { code: null, value: x.invoiceableItem.testQuotationLines[n]?.comment };
              analyses.isSubGroup = true;
            }
          }
        }
      }

      const packages =
        this.getPackageQuotationLines(x.invoiceableItem.packageQuotationLines.filter(pack => pack.groupCode === undefined));
      if (packages && packages.length > 0) {
        packages.forEach(pack => {
          sample.analysisDisplay.push(pack);
        });
        for (const n in x.invoiceableItem.packageQuotationLines) {
          if (x.invoiceableItem.packageQuotationLines[n].comment != null) {
            const analyses = sample.analysisDisplay.filter(a => a.displayName === x.invoiceableItem.packageQuotationLines[n].groupName
              && a.childDisplay.filter(p => p.code === x.invoiceableItem.packageQuotationLines[n].packageCode)[0])[0];
            if (analyses !== undefined) {
              analyses.structuredComment = new StandardComment();
              analyses.structuredComment = { code: null, value: x.invoiceableItem.packageQuotationLines[n]?.comment };
              analyses.isSubGroup = true;
            }
          }
        }
      }

      this.getCustomerPackages(sample.analysisDisplay, x.invoiceableItem.customerPackages);

      if (x.invoiceableItem.tests) {
        this.getTests(x.invoiceableItem.tests, sample.analysisDisplay);
      }
      if (x.invoiceableItem.packages) {
        this.getPackages(x.invoiceableItem.packages, sample.analysisDisplay);
      }

      sample.analysisDisplay = this.assignAnalysis(sample.analysisDisplay);
      this.getSampleSpecifications(sample, x);
      sample.attachDocuments = this.mapAttachments(attachments?.filter(y => y.sampleCode === sample.eurofinsSampleCode));
      sampleList.push(sample);
    });

    return sampleList;
  }

  private mapAttachments(files?: File[]): AttachDocument[] {
    const attachments: AttachDocument[] = [];
    if (files && files.length > 0) {
      files.forEach(x => {
        attachments.push({
          name: x.fileName,
          internalFileName: x.internalFileName,
          icon: this.attachDocumentHelperService.getIcon(x.fileName),
          status: x.scanStatus,
          fileStatus: x.status
        });
      });
    }
    return attachments;
  }

  private getmemberTests(memberTests: Test[]) {
    return memberTests?.map(j => ({
      sequence: j.sequence,
      code: j.code, childDisplay: j.memberTests?.map(k => ({
        sequence: k.sequence,
        code: k.code, childDisplay: []
        , isParent: undefined, price: undefined, eurodatKey: undefined,
        analysisType: undefined, displayName: k.localizedName === undefined || k.localizedName === '' ? k.name : k.localizedName
      }))
      , isParent: undefined, price: undefined, eurodatKey: undefined,
      analysisType: undefined, displayName: j.localizedName === undefined || j.localizedName === '' ? j.name : j.localizedName
    }));
  }

  private getTestQuotationLines(testQuotationLines: TestQuotationLine[]) {
    return testQuotationLines.map(i => ({
      sequence: i.sequence,
      code: i.testCode, comment: i.comment, groupCode: i.groupCode, groupName: i.groupName, childDisplay: this.getmemberTests(i.memberTests)
      , isParent: undefined, price: undefined, eurodatKey: undefined,
      analysisType: AnalysisType.FreeQLTest,
      displayName: i.text,
      isMrt: i.isMrt,
      executionNumber:i.executionNumber
    }));
  }

  private getTests(tests: Test[], analysisDisplayList: AnalysisDisplay[]) {
    const testAnalysis = tests.map(t => ({
      sequence: t.sequence,
      code: t.code, comment: t.comment, groupCode: t.testBundleCode, groupName: t.testBundleName,
      displayName: t.localizedName === undefined || t.localizedName === '' ? t.name : t.localizedName,
      childDisplay: this.getmemberTests(t.memberTests),
      structuredComment: { code: null, value: t.comment } as StandardComment,
      isParent: undefined, price: undefined, eurodatKey: undefined,
      analysisType: undefined,
      isMrt: t.isMrt,
      executionNumber:t.executionNumber
    }));

    const tempList = this.assignAnalysis(testAnalysis, true);
    if (analysisDisplayList?.length > 0) {
      tempList.forEach(x => {
        analysisDisplayList.push(x);
      });
    } else {
      analysisDisplayList.push(...tempList);
    }
  }

  private getPackages(packages: Package[], analysisDisplayList: AnalysisDisplay[]) {
    const packageAnalysis = packages.map(p => ({
      sequence: p.sequence,
      code: p.code, comment: p.comment,
      displayName: p.localizedName === undefined || p.localizedName === '' ? p.name : p.localizedName,
      structuredComment: { code: null, value: p.comment } as StandardComment,
      childDisplay: p.memberTests.map(i => ({
        code: i.code, childDisplay: this.getmemberTests(i.memberTests)
        , isParent: undefined, price: undefined, eurodatKey: undefined,
        analysisType: undefined, displayName: i.localizedName === undefined || i.localizedName === '' ? i.name : i.localizedName
      })),
      isParent: undefined, price: undefined, eurodatKey: undefined,
      analysisType: undefined
    }));

    if (analysisDisplayList?.length > 0) {
      packageAnalysis.forEach(x => {
        analysisDisplayList.push(x);
      });
    } else {
      analysisDisplayList.push(...packageAnalysis);
    }
  }

  private getPackageQuotationLines(
    packageQuotationLines: PackageQuotationLine[]) {

    return packageQuotationLines.map(p => ({
      sequence: p.sequence,
      code: p.packageCode, comment: p.comment, groupCode: p.groupCode, groupName: p.groupName,
      isParent: undefined, price: undefined, eurodatKey: undefined,
      analysisType: AnalysisType.FreeQLPackage,
      displayName: p.text,
      structuredComment: { code: '', value: p.comment },
      executionNumber: p.executionNumber,
      childDisplay: p.memberTests.map(i => ({
        code: i.code, childDisplay: this.getmemberTests(i.memberTests)
        , isParent: undefined, price: undefined, eurodatKey: undefined,
        analysisType: AnalysisType.FreeQLTest,
        displayName: i.localizedName === undefined || i.localizedName === '' ? i.name : i.localizedName
      }))
    }));
  }

  private getCustomerPackages(analysisDisplayList: AnalysisDisplay[], customerPackages: CustomerPackage[]) {
    customerPackages?.forEach(c => {
      const analysisDisplay: AnalysisDisplay = {
        analysisType: AnalysisType.CustomerPackage,
        childDisplay: [], code: '', displayName: '', isParent: undefined,
        price: undefined, eurodatKey: undefined,
        sequence: c.sequence
      };
      analysisDisplay.displayName = c.name;
      analysisDisplay.structuredComment = new StandardComment();
      analysisDisplay.structuredComment = { code: '', value: c.comment };
      analysisDisplay.executionNumber = c.executionNumber;
      analysisDisplay.childDisplay = this.getTestQuotationLines(c.testQuotationLines);
      const packages = this.getPackageQuotationLines(c.packageQuotationLines);
      packages.forEach(x => analysisDisplay.childDisplay.push(x));
      analysisDisplayList.push(analysisDisplay);
    });
  }

  private getSubGroups(
    analysisDisplayList: AnalysisDisplay[],
    testQL: TestQuotationLine[],
    packageQL: PackageQuotationLine[]) {

    var withMrtTests = testQL.filter(c => c.isMrt);
    var withoutMrtTests = testQL.filter(c => !c.isMrt);

    withMrtTests.forEach(tl => {
      if(tl.groupCode)
      {
        var group = {
              subgroupCode: tl.groupCode, code: tl.testCode, structuredComment: { code: '', value: tl.comment },
              analysisType: AnalysisType.SubgroupWithTest,
              childDisplay: [], isParent: undefined,
              price: undefined, eurodatKey: undefined,
              sequence: tl.sequence,
              displayName: tl.text,
              isMrt: tl.isMrt,
              executionNumber : tl.executionNumber ?? 1,
        }
        analysisDisplayList.push(group);
        group.childDisplay.push({
          sequence: tl.sequence,
          code: tl.testCode, structuredComment: { code: '', value: tl.comment },
          childDisplay: this.getmemberTests(tl.memberTests),
          isParent: undefined, price: undefined, eurodatKey: undefined,
          analysisType: AnalysisType.SubgroupWithTest,
          displayName: tl.text,
          isMrt: tl.isMrt,
          executionNumber : tl.executionNumber ?? 1,
        });
      }
    });

    withoutMrtTests.forEach(tl => {
      if(tl.groupCode){
        let group = analysisDisplayList.find(x => x.subgroupCode === tl.groupCode && !x.isMrt);
        if (!group)
        {
          group = {
            analysisType: AnalysisType.CustomerPackage,
            childDisplay: [], isParent: undefined,
            price: undefined, eurodatKey: undefined,
            subgroupCode: tl.groupCode, code: '',
            displayName: tl.groupName,
            sequence: tl.sequence,
            isMrt: tl.isMrt,
            executionNumber : tl.executionNumber ?? 1,
          };
          analysisDisplayList.push(group);
        }
        group.childDisplay.push({
          sequence: tl.sequence,
          code: tl.testCode, structuredComment: { code: '', value: tl.comment },
          childDisplay: this.getmemberTests(tl.memberTests),
          isParent: undefined, price: undefined, eurodatKey: undefined,
          analysisType: AnalysisType.SubgroupWithTest,
          displayName: tl.text,
          isMrt: tl.isMrt,
          executionNumber : tl.executionNumber ?? 1,
        });
      }
    });

    packageQL.forEach(pack => {
      if (pack.groupCode) {
        let group = analysisDisplayList.find(x => x.subgroupCode === pack.groupCode);
        if (!group) {
          group = {
            subgroupCode: pack.groupCode, code: '',
            analysisType: AnalysisType.CustomerPackage,
            childDisplay: [], isParent: undefined,
            price: undefined, eurodatKey: undefined,
            sequence: pack.sequence,
            displayName: pack.groupName,
            executionNumber : pack.executionNumber ?? 1,
          };

          analysisDisplayList.push(group);
        }

        group.childDisplay.push({
          sequence: pack.sequence,
          code: pack.packageCode, structuredComment: { code: '', value: pack.comment },
          childDisplay: this.getmemberTests(pack.memberTests),
          isParent: undefined, price: undefined, eurodatKey: undefined,
          analysisType: AnalysisType.SubgroupWithTest,
          displayName: pack.text,
          executionNumber : pack.executionNumber ?? 1,
        });
      }
    });

    return analysisDisplayList;
  }

  private getSampleSpecifications(sample: Sample, data: OrderSample) {
    sample.specifications = [];
    data?.specifications.forEach(x => {
      const param: ParameterSpecification = {
        parameterCode: undefined, parameterName: undefined, testName: undefined, testCode: undefined, analyses: undefined, required: undefined
      };
      const spec: SpecificationViewModel = { testName: '', testCode: '', parameters: undefined };
      spec.parameters = [];
      param.standardSpecification = {};
      param.standardSpecification.numeratorCode = { code: '', name: '' };
      param.standardSpecification.denominatorCode = { code: '', name: '' };
      const testName = x.testLocalizedName === undefined || x.testLocalizedName === '' ? x.testName : x.testLocalizedName;
      param.parameterName = x.parameterName;
      param.parameterCode = x.parameterCode;
      param.standardSpecification.numeratorCode.name = x.numeratorUnitName;
      param.standardSpecification.denominatorCode.name = x.denominatorUnitName;
      param.standardSpecification.target = x.expectedValue;
      if (x.lowerLimit !== undefined) {
        param.standardSpecification.min = x.lowerLimit;
      }
      if (x.upperLimit !== undefined) {
        param.standardSpecification.max = x.upperLimit;
      }
      const specification = sample.specifications.find(y => y.testName === testName);
      if (specification) {
        specification.parameters.push(param);
      } else {
        spec.testName = testName;
        spec.testCode = x.testCode;
        spec.parameters.push(param);
        sample.specifications.push(spec);
      }
    });
  }

  private assignAnalysis(analysisDisplay: AnalysisDisplay[], isTestBundle?: boolean) {
    const analysisWithoutGroup = _(analysisDisplay).filter((i: any) => i.groupCode === undefined).value();
    let analysisWithGroup: AnalysisDisplay[] = [];
    if (isTestBundle) {
      analysisWithGroup = _(analysisDisplay).filter((i: any) => i.groupCode !== undefined).groupBy((y: any) => y.groupCode)
        .map((key: any, value: any) => ({
          sequence: key[0].sequence,
          displayName: key[0].groupName, code: value, childDisplay: key
        })).value();
    } else {
      analysisWithGroup = _(analysisDisplay).filter((i: any) => i.groupCode !== undefined)
        .groupBy((y: any) => y.groupCode)
        .map((key: any) => ({
          sequence: key[0].sequence,
          displayName: key[0].groupName, childDisplay: key
        })).value();
    }
    analysisDisplay = [];
    if (analysisWithoutGroup.length > 0) {
      if (analysisDisplay.length === 0) {
        analysisDisplay.push(...analysisWithoutGroup);
      } else {
        analysisWithoutGroup.forEach((element: any) => {
          analysisDisplay.push(element);
        });
      }
    }

    if (analysisWithGroup.length > 0) {
      if (analysisDisplay.length === 0) {
        analysisDisplay.push(...analysisWithGroup);
      } else {
        analysisWithGroup.forEach((element: any) => {
          analysisDisplay.push(element);
        });
      }
    }

    return analysisDisplay;
  }

  private setOrderStatus(status: string) {
    return status === 'Validated' || 'InTransfer' || 'Transferred' || 'TransferFailed' || 'Received' || 'ImportFailed' ? TranslationService.translate(OrderStatus.Submitted) :
      status === 'Imported' ? TranslationService.translate(OrderStatus.ImportByLab) : '';
  }
}
